import { ChangeEvent, useEffect, useState } from "react";

import { Row } from "@components/Flex";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption } from "@components/Typography";
import { Translations, i18n } from "@i18n";
import { getDaysArray, isDayDisabled, isMonthDisabled } from "@utils";
import { useStyles } from "./styles";
import { DateInputProps } from "./types";

const MONTHS = [
  i18n.t(Translations.DateInputJanuary),
  i18n.t(Translations.DateInputFebruary),
  i18n.t(Translations.DateInputMarch),
  i18n.t(Translations.DateInputApril),
  i18n.t(Translations.DateInputMay),
  i18n.t(Translations.DateInputJune),
  i18n.t(Translations.DateInputJuly),
  i18n.t(Translations.DateInputAugust),
  i18n.t(Translations.DateInputSeptember),
  i18n.t(Translations.DateInputOctober),
  i18n.t(Translations.DateInputNovember),
  i18n.t(Translations.DateInputDecember),
];

const DEFAULT_MIN_YEAR = 1900;

export const DateInput = ({
  label,
  value,
  onChange,
  minimumDate,
  maximumDate,
  disabled = false,
}: DateInputProps) => {
  const styles = useStyles();

  const [day, setDay] = useState<number | undefined>(value?.getDate());
  const [month, setMonth] = useState<number | undefined>(value?.getMonth());
  const [year, setYear] = useState<number | undefined>(value?.getFullYear());

  useEffect(() => {
    if (day != null && month != null && year != null) {
      onChange(new Date(year, month, day));
    }
  }, [day, month, year]);

  const yearOptions = (() => {
    const maxYear = maximumDate?.getFullYear() ?? new Date().getFullYear();
    const minYear = minimumDate?.getFullYear() ?? DEFAULT_MIN_YEAR;

    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  })();

  const dayOptions = getDaysArray(year, month);

  const onDayChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setDay(Number(event.target.value));
  };

  const onMonthChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setMonth(Number(event.target.value));
    setDay(undefined);
  };

  const onYearChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setYear(Number(event.target.value));

    const monthOption: HTMLSelectElement | null = document.getElementById(
      "month",
    ) as HTMLSelectElement | null;
    const dayOption = document.getElementById("day") as HTMLSelectElement | null;

    if (monthOption) {
      monthOption.selectedIndex = 0;
    }
    if (dayOption) {
      dayOption.selectedIndex = 0;
    }

    setMonth(undefined);
    setDay(undefined);
  };

  const disabledStyle = disabled ? styles.disabled : undefined;
  const dayEmptyStyle = day == null ? styles.disabled : undefined;
  const monthEmptyStyle = month == null ? styles.disabled : undefined;
  const yearEmptyStyle = year == null ? styles.disabled : undefined;

  return (
    <div style={{ ...styles.container, ...disabledStyle }}>
      <Row>
        <div style={styles.inner}>
          <Caption>{label}</Caption>
          <Spacing size={SpacingSizes.XXS} />

          <Row>
            <Row style={{ ...styles.year, ...yearEmptyStyle }}>
              <select
                id="year"
                name="year"
                style={styles.input}
                value={year}
                onChange={onYearChange}
                disabled={disabled}
              >
                <option value="" disabled selected>
                  {i18n.t(Translations.DateInputYear)}
                </option>
                {yearOptions.map((y) => (
                  <option value={y}>{y}</option>
                ))}
              </select>
              <span style={styles.divider}>|</span>
            </Row>

            <Row style={{ ...styles.month, ...monthEmptyStyle }}>
              <select
                id="month"
                name="month"
                style={styles.input}
                value={month}
                onChange={onMonthChange}
                disabled={disabled || year == null}
              >
                <option value="" disabled selected>
                  {i18n.t(Translations.DateInputMonth)}
                </option>
                {MONTHS.map((m, index) => (
                  <option
                    value={index.toString()}
                    disabled={isMonthDisabled(maximumDate, minimumDate, index, year)}
                  >
                    {m}
                  </option>
                ))}
              </select>
              <span style={styles.divider}>|</span>
            </Row>

            <Row style={{ ...styles.day, ...dayEmptyStyle }}>
              <select
                id="day"
                name="day"
                style={styles.input}
                value={day}
                onChange={onDayChange}
                disabled={disabled || month == null}
              >
                <option value="" disabled selected>
                  {i18n.t(Translations.DateInputDay)}
                </option>
                {dayOptions.map((d) => (
                  <option
                    value={d}
                    disabled={isDayDisabled(maximumDate, minimumDate, d, month, year)}
                  >
                    {d}
                  </option>
                ))}
              </select>
            </Row>
          </Row>
          <Spacing size={SpacingSizes.XXS} />
        </div>
      </Row>
    </div>
  );
};
